/**
 * Part-renderer registry (PR 3 of response-formatting design §E).
 *
 * Maps `UIMessagePart.type` to a React component. PR 3 covers the
 * parity scope only (`text` + `tool-call` + `tool-result`); the
 * registry has a fallback that renders a debug placeholder for any
 * unknown part type so a backend addition lands gracefully on stale
 * frontends. Later PRs add:
 *   - `data-structured-response` (PR 5b)
 *   - `data-timeseries` (PR 6)
 *   - `reasoning` with supersededAtFinish (PR 6b)
 *   - `data-citation`, `data-chart`
 *
 * Adding a new part type is one schema entry in
 * `@intuigence/response-parts` + one component here + one map entry.
 */
import { Component, useMemo, useState, type ComponentType, type ErrorInfo, type ReactNode } from 'react'
import ReactMarkdown from 'react-markdown'
import { useTranslation } from '@/i18n'
import type { PluggableList } from 'unified'
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import type {
  ChartPart,
  CitationPart,
  ReasoningPart,
  StructuredResponsePart,
  TimeseriesPart,
  ToolCallPart,
  ToolResultPart,
  PendingSharePart,
  UIMessagePart,
  WorkflowProgressPart,
} from '@intuigence/response-parts'

import {
  remarkPlugins as remarkPluginsConfig,
  rehypePlugins as rehypePluginsConfig,
  mergeMarkdownComponents,
} from '../../../config/markdownConfig'
import { SourcesFooter } from '../components/messages/SourcesFooter'
import { useAppStore } from '@/store'
import { IntuiAvatar } from '@/components/intueri/IntuiAvatar'
import type { SourcePart } from '../types'

// `markdownConfig` exports these as untyped array literals, so TS widens the
// element union instead of inferring `PluggableList`. The values genuinely
// conform to `PluggableList` ([plugin, options] tuples are valid `Pluggable`s);
// assert to the precise type at the import boundary. Root fix belongs in
// markdownConfig (see crossFileNeeds): annotate the exports as `PluggableList`.
const remarkPlugins = remarkPluginsConfig as PluggableList
const rehypePlugins = rehypePluginsConfig as PluggableList

interface PartRendererProps<P extends UIMessagePart = UIMessagePart> {
  part: P
}

/**
 * Markdown text renderer (PR 16 — closes design §E gap "TextPart
 * (react-markdown, GFM, math)").
 *
 * Server-side normalization (PR 4) cleans LaTeX delimiters, fence
 * whitespace, and citation paths before the text reaches us, so the
 * legacy frontend safety-net patches (`AssistantMessage.tsx:31`,
 * `StructuredResponse.tsx:20-37`) are unnecessary on the v2 path —
 * this renderer trusts its input. The plugin set mirrors the project
 * standard from `markdownConfig.tsx`: GFM, math, KaTeX. The KaTeX
 * `errorCallback` wired in PR 9 picks up any surviving render failure
 * via `[KATEX_ERROR]`.
 *
 * Why a wrapping div instead of bare ReactMarkdown: the parts
 * dispatcher treats every renderer as a flow item; without a
 * container, GFM tables and code blocks bleed CSS-margin into
 * adjacent parts. The `prose` Tailwind classes mirror the legacy
 * AssistantMessage's wrapper so v1 and v2 traffic look the same.
 */
function TextPartRenderer({ part }: PartRendererProps<{ type: 'text'; text: string }>) {
  return (
    <div
      data-testid="text-part"
      className="prose prose-sm max-w-none text-gray-900 dark:text-dark-text dark:prose-invert"
    >
      <ReactMarkdown
        remarkPlugins={remarkPlugins}
        rehypePlugins={rehypePlugins}
        components={mergeMarkdownComponents()}
      >
        {part.text}
      </ReactMarkdown>
    </div>
  )
}

/**
 * Reasoning renderer (PR 6b). Two modes:
 *   - supersededAtFinish=true: collapsed by default, showing
 *     finalSummary; the verbose preliminaries expand on click
 *     (the "stale 'unable to find an answer' reasoning" bug).
 *   - else: expanded by default. Same component handles both;
 *     defaultOpen is the only difference, so reopened-chat
 *     persistence stays a single object (design §G "Lean
 *     toward annotation").
 */
function ReasoningRenderer({ part }: PartRendererProps<ReasoningPart>) {
  const { t } = useTranslation()
  const collapsed = part.supersededAtFinish === true
  const [open, setOpen] = useState(!collapsed)
  return (
    <details
      data-testid="reasoning-part"
      data-superseded={collapsed ? 'true' : 'false'}
      open={open}
      onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
    >
      <summary data-testid="reasoning-summary">
        {collapsed && part.finalSummary ? part.finalSummary : t('chatv2:messages.thinking_ellipsis', { defaultValue: 'Thinking…' })}
      </summary>
      <pre data-testid="reasoning-text">{part.text}</pre>
    </details>
  )
}

function ToolCallRenderer({ part }: PartRendererProps<ToolCallPart>) {
  return (
    <div data-testid="tool-call-part" data-tool-call-id={part.toolCallId} data-tool-name={part.toolName}>
      <code>{part.toolName}(…)</code>
    </div>
  )
}

function ToolResultRenderer({ part }: PartRendererProps<ToolResultPart>) {
  return (
    <div
      data-testid="tool-result-part"
      data-tool-call-id={part.toolCallId}
      data-is-error={part.isError ? 'true' : 'false'}
    >
      <code>{part.toolName} ✓</code>
    </div>
  )
}

/**
 * Engineering-style stepped answer (PR 5b). Replaces the legacy
 * `` ```json:structured_response `` markdown-fence regex parser at
 * AssistantMessage.tsx:46. Inputs are typed (Zod-parsed at the wire
 * boundary by the backend), so this renderer can index fields
 * directly without defensive checks.
 */
function StructuredResponseRenderer({
  part,
}: PartRendererProps<StructuredResponsePart>) {
  return (
    <section data-testid="structured-response-part" className="structured-response">
      <header data-testid="structured-response-problem">{part.problem}</header>
      {part.given.length > 0 && (
        <dl data-testid="structured-response-given">
          {part.given.map((g, i) => (
            <div key={i}>
              <dt>{g.label}</dt>
              <dd>
                {g.value}
                {g.unit ? ` ${g.unit}` : ''}
              </dd>
            </div>
          ))}
        </dl>
      )}
      <ol data-testid="structured-response-steps">
        {part.steps.map((s, i) => (
          <li key={i}>
            <strong>{s.title}</strong>
            <p>{s.explanation}</p>
            {s.expression ? <code>{s.expression}</code> : null}
          </li>
        ))}
      </ol>
      <footer data-testid="structured-response-final-answer">
        {part.finalAnswer.value}
        {part.finalAnswer.unit ? ` ${part.finalAnswer.unit}` : ''}
      </footer>
    </section>
  )
}

/**
 * Timeseries renderer (PR 6 + PR 16 chart compile per design §D).
 *
 * Three pieces:
 *   1. Recharts line chart — only when `series` is present. Renderer
 *      compiles the visualization from the typed shape; the model
 *      stays out of the visualization layer (per design's open-
 *      question resolution).
 *   2. Stats card — min/max/mean/p50/p95.
 *   3. Manifest-gap banner — unconditional when `manifestGaps` is
 *      non-empty. FM8 abstain rule made deterministic at the
 *      renderer.
 */
function TimeseriesRenderer({ part }: PartRendererProps<TimeseriesPart>) {
  const { t: translate } = useTranslation()
  // Recharts wants `{t, v}` tuples with `t` as a renderable label.
  // The typed shape already has the right keys; map nothing.
  const series = part.series ?? []
  return (
    <section data-testid="timeseries-part" className="timeseries-part">
      <header data-testid="timeseries-tag-id">{part.tagId}</header>
      <div data-testid="timeseries-range">
        {part.range.start} → {part.range.end}
      </div>
      {series.length > 0 && (
        <div data-testid="timeseries-chart" style={{ width: '100%', height: 200 }}>
          <ResponsiveContainer>
            <LineChart data={series}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="t" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="v" dot={false} stroke="#1A1A1A" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      <dl data-testid="timeseries-stats">
        <div>
          <dt>min</dt>
          <dd>{part.stats.min}</dd>
        </div>
        <div>
          <dt>max</dt>
          <dd>{part.stats.max}</dd>
        </div>
        <div>
          <dt>mean</dt>
          <dd>{part.stats.mean}</dd>
        </div>
        <div>
          <dt>p50</dt>
          <dd>{part.stats.p50}</dd>
        </div>
        <div>
          <dt>p95</dt>
          <dd>{part.stats.p95}</dd>
        </div>
      </dl>
      {part.manifestGaps.length > 0 && (
        <aside
          data-testid="timeseries-manifest-gap-banner"
          role="alert"
          className="timeseries-manifest-gap-banner"
        >
          {translate('chatv2:timeseries.data_missing', { fields: part.manifestGaps.join(', '), defaultValue: 'Data missing for: {{fields}}' })}
        </aside>
      )}
    </section>
  )
}

/**
 * Citation renderer (PR 8 gap closure). A typed reference card with a
 * source-type badge so the user understands "where this comes from"
 * without parsing free-text path tokens. Replaces inline path
 * mentions like `packages/sheets/X/index.ts` once the agent emits
 * citations as data parts (paired with the citation_rewrite rule
 * which strips the prose form).
 */
function CitationRenderer({ part }: PartRendererProps<CitationPart>) {
  return (
    <a
      data-testid="citation-part"
      data-source-type={part.sourceType}
      href={part.href}
      className="citation-part"
    >
      <span data-testid="citation-label">{part.label}</span>
      {part.snippet ? (
        <small data-testid="citation-snippet">{part.snippet}</small>
      ) : null}
    </a>
  )
}

/**
 * Chart renderer (PR 8 + PR 16 chart compile per design §D / open-
 * question resolution). The typed `ChartPart` carries
 * `(tagId, range, chartType, stats?)`. We compile to a recharts
 * line chart with the stats values as datapoints anchored at the
 * range endpoints, so the chart shape is meaningful even before a
 * full series is supplied (a follow-up that fetches the underlying
 * series from the historian replaces the stats-anchor with the real
 * data).
 *
 * Why anchor at endpoints rather than render an empty chart: the
 * design says "the renderer compiles the visualization, keeping
 * the model out of the visualization layer". An anchor-based
 * placeholder shows shape (range + min/max bounds) without forcing
 * the model to ship a series array.
 */
function ChartRenderer({ part }: PartRendererProps<ChartPart>) {
  const stats = part.stats
  // Two-point anchor when stats are present: min at start, max at
  // end. Renderable shape; replaced by real series in a follow-up.
  const data = stats
    ? [
        { t: part.range.start, v: stats.min },
        { t: part.range.end, v: stats.max },
      ]
    : []
  return (
    <figure
      data-testid="chart-part"
      data-tag-id={part.tagId}
      data-chart-type={part.chartType}
    >
      <figcaption>
        {part.tagId} ({part.chartType})
      </figcaption>
      <span data-testid="chart-range">
        {part.range.start} → {part.range.end}
      </span>
      {data.length > 0 && (
        <div data-testid="chart-canvas" style={{ width: '100%', height: 180 }}>
          <ResponsiveContainer>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="t" />
              <YAxis />
              <Tooltip />
              <Line type="linear" dataKey="v" dot stroke="#1A1A1A" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </figure>
  )
}

function WorkflowProgressRenderer({ part }: PartRendererProps<WorkflowProgressPart>) {
  // Chat shows a one-line status strip only. The execution graph and the
  // Workflow/Configure panels live exclusively on /workflow/new (see
  // docs/workflow-new-page-fit.md: the page is the only product frontend) —
  // an earlier revision embedded a 680px ReactFlow mock of that page into
  // every chat message. The streamed chat text narrates the run; this strip
  // just pins final status + the link for persisted messages.
  const safePart = useMemo(() => normalizeWorkflowProgressPart(part), [part])
  if (!safePart) {
    return (
      <section
        data-testid="workflow-progress-part"
        className="my-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100"
      >
        <div className="font-semibold">Troubleshooting workflow</div>
        <div className="mt-1">
          Workflow progress is unavailable for this message. The final answer can still render below.
        </div>
      </section>
    )
  }

  const completedCount = safePart.phases.filter((phase) => phase.status === 'completed').length
  return (
    <section
      data-testid="workflow-progress-part"
      className="my-3 flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm dark:border-dark-border dark:bg-dark-surface dark:text-dark-text"
    >
      <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-sky-50 text-[11px] font-semibold text-sky-700 dark:bg-sky-950 dark:text-sky-300">
        WF
      </span>
      <span className="font-medium">Troubleshooting workflow</span>
      <span className="text-xs text-gray-500 dark:text-dark-muted">
        {completedCount}/{safePart.phases.length} phases
      </span>
      <span className="rounded-md border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-700 dark:border-dark-border dark:bg-dark-surface-elevated dark:text-dark-muted">
        {safePart.status}
      </span>
      <a
        data-testid="workflow-progress-open-page"
        href={`/workflow/new?runId=${encodeURIComponent(safePart.workflowRunId)}`}
        className="ml-auto rounded-md border border-gray-200 px-2.5 py-1 text-xs font-medium text-sky-700 hover:bg-sky-50 dark:border-dark-border dark:text-sky-300 dark:hover:bg-dark-surface-elevated"
      >
        Open workflow page
      </a>
    </section>
  )
}

function normalizeWorkflowProgressPart(part: WorkflowProgressPart): WorkflowProgressPart | null {
  const raw = part as unknown;
  if (!raw || typeof raw !== 'object') return null;

  const candidate = raw as Record<string, unknown>;
  if (candidate.type !== 'data-workflow-progress') return null;
  if (typeof candidate.workflowRunId !== 'string' || candidate.workflowRunId.length === 0) return null;
  if (typeof candidate.status !== 'string' || candidate.status.length === 0) return null;
  if (!Array.isArray(candidate.phases) || candidate.phases.length === 0) return null;

  const phases = candidate.phases
    .map((phase, phaseIndex) => normalizeWorkflowProgressPhase(phase, phaseIndex))
    .filter((phase): phase is WorkflowProgressPart['phases'][number] => phase !== null)
  if (phases.length === 0) return null;

  return {
    ...(candidate as WorkflowProgressPart),
    currentPhaseId:
      typeof candidate.currentPhaseId === 'string' && candidate.currentPhaseId.length > 0
        ? candidate.currentPhaseId
        : undefined,
    updatedAt:
      typeof candidate.updatedAt === 'string' && candidate.updatedAt.length > 0
        ? candidate.updatedAt
        : new Date(0).toISOString(),
    phases,
  }
}

function normalizeWorkflowProgressPhase(
  phase: unknown,
  phaseIndex: number,
): WorkflowProgressPart['phases'][number] | null {
  if (!phase || typeof phase !== 'object') return null;
  const candidate = phase as Record<string, unknown>;
  if (typeof candidate.id !== 'string' || candidate.id.length === 0) return null;
  if (typeof candidate.title !== 'string' || candidate.title.length === 0) return null;
  const phaseId = candidate.id;
  const title = candidate.title;

  return {
    id: phaseId,
    title,
    status: normalizeWorkflowPhaseStatus(candidate.status),
    summary: typeof candidate.summary === 'string' ? candidate.summary : undefined,
    evidenceCount: nonNegativeIntOrUndefined(candidate.evidenceCount),
    toolCallCount: nonNegativeIntOrUndefined(candidate.toolCallCount),
    tools: Array.isArray(candidate.tools)
      ? candidate.tools
          .map((tool, toolIndex) => normalizeWorkflowProgressTool(tool, phaseId, phaseIndex, toolIndex))
          .filter((tool): tool is NonNullable<WorkflowProgressPart['phases'][number]['tools']>[number] => tool !== null)
      : undefined,
  }
}

function normalizeWorkflowProgressTool(
  tool: unknown,
  phaseId: string,
  phaseIndex: number,
  toolIndex: number,
): NonNullable<WorkflowProgressPart['phases'][number]['tools']>[number] | null {
  if (!tool || typeof tool !== 'object') return null;
  const candidate = tool as Record<string, unknown>;
  if (typeof candidate.name !== 'string' || candidate.name.length === 0) return null;

  return {
    id:
      typeof candidate.id === 'string' && candidate.id.length > 0
        ? candidate.id
        : `${phaseId}-tool-${phaseIndex}-${toolIndex}`,
    name: candidate.name,
    status: normalizeWorkflowToolStatus(candidate.status),
    inputPreview: typeof candidate.inputPreview === 'string' ? candidate.inputPreview : undefined,
    outputPreview: typeof candidate.outputPreview === 'string' ? candidate.outputPreview : undefined,
    errorPreview: typeof candidate.errorPreview === 'string' ? candidate.errorPreview : undefined,
  }
}

function normalizeWorkflowPhaseStatus(
  status: unknown,
): WorkflowProgressPart['phases'][number]['status'] {
  switch (status) {
    case 'pending':
    case 'running':
    case 'waiting':
    case 'completed':
    case 'blocked':
    case 'failed':
    case 'skipped':
      return status
    default:
      return 'pending'
  }
}

function normalizeWorkflowToolStatus(
  status: unknown,
): NonNullable<WorkflowProgressPart['phases'][number]['tools']>[number]['status'] {
  switch (status) {
    case 'running':
    case 'ok':
    case 'error':
      return status
    default:
      return 'running'
  }
}

function nonNegativeIntOrUndefined(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : undefined
}

/** Fallback for unknown part types. Not silent: logs once per render
 *  with the offending type so backend additions surface in the
 *  browser console until a renderer ships. */
function UnknownPartRenderer({ part }: { part: UIMessagePart }) {
  const { t } = useTranslation()
  // eslint-disable-next-line no-console
  console.warn('[FORMAT_PARSE_FAILED] no renderer for part.type', part.type)
  return (
    <div data-testid="unknown-part" data-part-type={part.type}>
      {t('chatv2:messages.unrecognized_section', { defaultValue: '[unrecognized response section]' })}
    </div>
  )
}

/**
 * FS-663: the confirm card for a share the agent PREPARED but did not send.
 *
 * This component is the ONLY thing that can complete an agent-initiated share:
 * `share_with` has no send path, so until the user clicks Send here, nothing
 * has been granted and no email has gone out. The recipient's EMAIL is shown,
 * not just their name — "the wrong Ammar" has to be visible at the moment of
 * the decision.
 */
function PendingShareRenderer({ part }: PartRendererProps<PendingSharePart>) {
  const { t } = useTranslation()
  const createShares = useAppStore((s) => s.createShares)
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'dismissed'>('idle')
  const [copied, setCopied] = useState(false)
  // Why the last attempt did not simply succeed, so the card can say so.
  const [notice, setNotice] = useState<
    'invited' | 'invite_required' | 'email_failed' | 'error' | null
  >(null)

  if (state === 'dismissed') return null

  const { recipient, resourceName, url, resourceType, resourceId } = part

  const handleSend = async () => {
    setState('sending')
    const result = await createShares({
      resource_type: resourceType,
      resource_id: resourceId,
      emails: [recipient.email],
    })

    // A 2xx does NOT mean the recipient was told (greptile on #27487). The
    // per-recipient status distinguishes "granted and notified" from "granted
    // but we could not email them" and "an admin has to invite them" — so only
    // the first two count as sent, and anything else stays retryable rather
    // than claiming success the user cannot verify.
    const outcome = result?.results?.[0]?.status
    const granted = outcome === 'shared' || outcome === 'resent' || outcome === 'invited'
    if (granted) {
      setState('sent')
      setNotice(outcome === 'invited' ? 'invited' : null)
    } else {
      setState('idle')
      setNotice(outcome === 'invite_required' ? 'invite_required' : outcome === 'email_failed' ? 'email_failed' : 'error')
    }
  }

  return (
    <div className="my-2 rounded-lg border border-border bg-card p-4">
      <p className="mb-3 text-sm text-muted-foreground">
        {t('chatv2:pending_share.title', { defaultValue: 'Ready to share' })}
      </p>

      <div className="mb-3 flex items-center gap-3">
        <IntuiAvatar
          size="sm"
          {...(recipient.picture ? { src: recipient.picture } : {})}
          fallback={(recipient.name || recipient.email).slice(0, 2).toUpperCase()}
        />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{recipient.name || recipient.email}</p>
          {/* Always render the address, even when a name is available. */}
          <p className="truncate text-xs text-muted-foreground">{recipient.email}</p>
        </div>
      </div>

      <p className="mb-3 truncate text-sm">{resourceName}</p>

      {notice && state !== 'sent' && (
        <p className="mb-2 text-sm text-amber-700 dark:text-amber-400">
          {notice === 'invite_required'
            ? t('chatv2:pending_share.invite_required', {
                defaultValue: 'An administrator needs to invite them first.',
              })
            : notice === 'email_failed'
              ? t('chatv2:pending_share.email_failed', {
                  defaultValue: "They now have access, but we couldn't email them.",
                })
              : t('chatv2:pending_share.error', {
                  defaultValue: "Couldn't share. Please try again.",
                })}
        </p>
      )}

      {state === 'sent' ? (
        <p className="text-sm text-green-700 dark:text-green-400">
          {notice === 'invited'
            ? t('chatv2:pending_share.invited', {
                defaultValue: 'Shared — they have been invited to join.',
              })
            : t('chatv2:pending_share.sent', { defaultValue: 'Shared.' })}
        </p>
      ) : (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleSend}
            disabled={state === 'sending'}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-60"
          >
            {state === 'sending'
              ? t('chatv2:pending_share.sending', { defaultValue: 'Sending…' })
              : t('chatv2:pending_share.send', { defaultValue: 'Send' })}
          </button>
          <button
            type="button"
            onClick={() => {
              void navigator.clipboard?.writeText(url)
              setCopied(true)
              setTimeout(() => setCopied(false), 2000)
            }}
            className="rounded-md border border-border px-3 py-1.5 text-sm"
          >
            {copied
              ? t('chatv2:pending_share.copied', { defaultValue: 'Copied' })
              : t('chatv2:pending_share.copy_link', { defaultValue: 'Copy link' })}
          </button>
          <button
            type="button"
            onClick={() => setState('dismissed')}
            className="rounded-md px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            {t('chatv2:pending_share.dismiss', { defaultValue: 'Dismiss' })}
          </button>
        </div>
      )}
    </div>
  )
}

/** Public registry. Component-level fallback at lookup time. */
export const partRenderers: Partial<Record<UIMessagePart['type'], ComponentType<PartRendererProps>>> = {
  text: TextPartRenderer as ComponentType<PartRendererProps>,
  reasoning: ReasoningRenderer as ComponentType<PartRendererProps>,
  'tool-call': ToolCallRenderer as ComponentType<PartRendererProps>,
  'tool-result': ToolResultRenderer as ComponentType<PartRendererProps>,
  'data-structured-response': StructuredResponseRenderer as ComponentType<PartRendererProps>,
  'data-timeseries': TimeseriesRenderer as ComponentType<PartRendererProps>,
  'data-citation': CitationRenderer as ComponentType<PartRendererProps>,
  'data-chart': ChartRenderer as ComponentType<PartRendererProps>,
  'data-workflow-progress': WorkflowProgressRenderer as ComponentType<PartRendererProps>,
  'data-pending-share': PendingShareRenderer as ComponentType<PartRendererProps>,
}

/** Choose the renderer for a part, falling back when none is registered. */
export function getRendererForPart(
  part: UIMessagePart,
): ComponentType<PartRendererProps> {
  return partRenderers[part.type] ?? (UnknownPartRenderer as ComponentType<PartRendererProps>)
}

export interface RenderedAssistantMessageProps {
  parts: UIMessagePart[]
}

class PartRenderErrorBoundary extends Component<
  { partType: string; children: ReactNode },
  { failed: boolean }
> {
  constructor(props: { partType: string; children: ReactNode }) {
    super(props)
    this.state = { failed: false }
  }

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('[FORMAT_PARSE_FAILED] part renderer crashed', {
      partType: this.props.partType,
      error,
      componentStack: info.componentStack,
    })
  }

  render() {
    if (this.state.failed) {
      return (
        <div
          data-testid="part-render-error"
          data-part-type={this.props.partType}
          className="my-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100"
        >
          This response section could not be displayed.
        </div>
      )
    }

    return this.props.children
  }
}

/** Top-level dispatcher. AssistantMessage in chatV2 currently does
 *  free-form markdown extraction; once the registry covers all part
 *  types this component replaces that path entirely (PR 7). */
export function RenderedAssistantMessage({ parts }: RenderedAssistantMessageProps) {
  // `source` parts are message-level citations, not flow content: split
  // them out so they render once as the shared Sources footer rather
  // than inline through the unknown-part fallback. They reach us via
  // buildV2StreamParts (live `data-kg-sources` snapshot) and
  // Message.sources hydration (reload), so the V3 path shows the same
  // card the legacy AssistantMessage renders.
  const flowParts: UIMessagePart[] = []
  const sourceParts: SourcePart[] = []
  for (const part of parts) {
    if ((part as { type?: string }).type === 'source') {
      sourceParts.push(part as unknown as SourcePart)
    } else {
      flowParts.push(part)
    }
  }
  return (
    <div data-testid="rendered-assistant-message">
      {flowParts.map((part, index) => {
        const Renderer = getRendererForPart(part)
        return (
          <PartRenderErrorBoundary key={index} partType={(part as { type?: string }).type ?? 'unknown'}>
            <Renderer part={part} />
          </PartRenderErrorBoundary>
        )
      })}
      <SourcesFooter sourceParts={sourceParts} />
    </div>
  )
}
